/**
 * useMobile Hook
 *
 * This hook tells you if someone is using a phone or tablet.
 *
 * HOW TO USE:
 *
 * 1. Import it in your component:
 *    import { useMobile } from '../hooks/useMobile';
 *
 * 2. Use it inside your component:
 *    const { isMobile, isTablet, isTouch, screenSize } = useMobile();
 *
 * 3. Now you can do things like:
 *    {isMobile && <MobileNavBar />}
 *    {!isMobile && <DesktopSidebar />}
 */

import { useState, useEffect } from "react";

// Screen size breakpoints (matches Tailwind CSS defaults)
const BREAKPOINTS = {
  sm: 640, // Small phones
  md: 768, // Tablets / Large phones in landscape
  lg: 1024, // Small laptops
  xl: 1280, // Desktops
  "2xl": 1536, // Large desktops
};

export function useMobile() {
  // Initialize with safe defaults (assumes desktop for server-side rendering)
  const [state, setState] = useState({
    isMobile: false,
    isTablet: false,
    isDesktop: true,
    isTouch: false,
    screenSize: "lg",
    width: typeof window !== "undefined" ? window.innerWidth : 1024,
    height: typeof window !== "undefined" ? window.innerHeight : 768,
    orientation: "landscape",
  });

  useEffect(() => {
    // This function figures out what device someone is using
    const updateDeviceInfo = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;

      // Check if the device supports touch
      const isTouch =
        "ontouchstart" in window ||
        navigator.maxTouchPoints > 0 ||
        (window.matchMedia && window.matchMedia("(pointer: coarse)").matches);

      // Figure out the screen size category
      let screenSize = "2xl";
      if (width < BREAKPOINTS.sm) screenSize = "xs";
      else if (width < BREAKPOINTS.md) screenSize = "sm";
      else if (width < BREAKPOINTS.lg) screenSize = "md";
      else if (width < BREAKPOINTS.xl) screenSize = "lg";
      else if (width < BREAKPOINTS["2xl"]) screenSize = "xl";

      // Determine device type
      const isMobile = width < BREAKPOINTS.md;
      const isTablet = width >= BREAKPOINTS.md && width < BREAKPOINTS.lg;
      const isDesktop = width >= BREAKPOINTS.lg;

      // Determine orientation
      const orientation = width > height ? "landscape" : "portrait";

      setState({
        isMobile,
        isTablet,
        isDesktop,
        isTouch,
        screenSize,
        width,
        height,
        orientation,
      });
    };

    // Run once immediately when the component loads
    updateDeviceInfo();

    // Listen for window resize events
    window.addEventListener("resize", updateDeviceInfo);

    // Also listen for orientation changes (important for tablets/phones)
    window.addEventListener("orientationchange", () => {
      // Small delay to let the browser update dimensions
      setTimeout(updateDeviceInfo, 100);
    });

    // Cleanup: remove the listeners when the component unmounts
    return () => {
      window.removeEventListener("resize", updateDeviceInfo);
      window.removeEventListener("orientationchange", updateDeviceInfo);
    };
  }, []);

  return state;
}

/**
 * useMediaQuery Hook
 *
 * A simpler hook for checking specific screen sizes.
 *
 * HOW TO USE:
 * const isSmallScreen = useMediaQuery('(max-width: 640px)');
 * const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');
 */
export function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => {
    if (typeof window !== "undefined") {
      return window.matchMedia(query).matches;
    }
    return false;
  });

  useEffect(() => {
    // Create a MediaQueryList object
    const mediaQuery = window.matchMedia(query);

    // Update matches if it changed since init (e.g. hydration mismatch handled by React, but good to be safe)
    if (mediaQuery.matches !== matches) {
      setTimeout(() => setMatches(mediaQuery.matches), 0);
    }

    // Define the handler
    const handler = (event) => setMatches(event.matches);

    // Modern browsers use addEventListener
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handler);
    } else {
      // Older browsers use addListener
      mediaQuery.addListener(handler);
    }

    // Cleanup
    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener("change", handler);
      } else {
        mediaQuery.removeListener(handler);
      }
    };
  }, [query, matches]);

  return matches;
}

/**
 * Shortcut hooks for common checks
 */
export const useIsMobile = () => useMediaQuery("(max-width: 767px)");
export const useIsTablet = () =>
  useMediaQuery("(min-width: 768px) and (max-width: 1023px)");
export const useIsDesktop = () => useMediaQuery("(min-width: 1024px)");
export const useIsTouch = () =>
  useMediaQuery("(hover: none) and (pointer: coarse)");
export const usePrefersDarkMode = () =>
  useMediaQuery("(prefers-color-scheme: dark)");
export const usePrefersReducedMotion = () =>
  useMediaQuery("(prefers-reduced-motion: reduce)");

export default useMobile;
